import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ChevronDownIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';

/* Reusable FAQ accordion. Matches the dashboard redesign handoff:
   white card, brand-yellow chevron circle when open. Content comes from
   i18n (`faq.eyebrow`, `faq.title`, `faq.sections` → [{ label, items: [{ q, a }] }]). */

const Eyebrow = ({ children }) => (
  <div className="text-[11px] uppercase tracking-[0.14em] text-muted font-bold mb-1.5">
    {children}
  </div>
);

const Faq = ({ className = '' }) => {
  const { t } = useTranslation();
  // open item keyed by "sectionIndex-itemIndex"; only one open at a time.
  const [open, setOpen] = useState(null);

  const sections = t('faq.sections', { returnObjects: true });
  const list = Array.isArray(sections) ? sections : [];

  if (!list.length) return null;

  return (
    <section
      className={`bg-white border border-line rounded-2xl shadow-softer p-6 ${className}`}
    >
      <header className="mb-5">
        <Eyebrow>{t('faq.eyebrow')}</Eyebrow>
        <h3 className="text-[17px] sm:text-[19px] font-bold tracking-tight text-ink-strong leading-snug">
          {t('faq.title')}
        </h3>
      </header>

      <div className="space-y-7">
        {list.map((section, si) => {
          const items = Array.isArray(section.items) ? section.items : [];
          if (!items.length) return null;
          return (
            <div key={si}>
              {section.label && (
                <h4 className="text-[12px] uppercase tracking-[0.08em] text-yellow-05 font-bold mb-1 pb-2 border-b border-line">
                  {section.label}
                </h4>
              )}
              <ul className="divide-y divide-line">
                {items.map((item, ii) => {
                  const key = `${si}-${ii}`;
                  const isOpen = open === key;
                  return (
                    <li key={key}>
                      <button
                        type="button"
                        onClick={() => setOpen(isOpen ? null : key)}
                        aria-expanded={isOpen}
                        className="w-full text-left py-4 flex items-center gap-4"
                      >
                        <span
                          className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 transition-colors ${
                            isOpen
                              ? 'bg-brand text-ink-strong'
                              : 'bg-brand-soft text-brand-deep'
                          }`}
                        >
                          {isOpen ? (
                            <ChevronDownIcon className="w-3.5 h-3.5" />
                          ) : (
                            <ChevronRightIcon className="w-3.5 h-3.5" />
                          )}
                        </span>
                        <span className="text-[14.5px] font-bold text-ink-strong flex-1">
                          {item.q}
                        </span>
                      </button>
                      {isOpen && (
                        <div className="pb-4 pl-11 text-[13.5px] text-muted leading-relaxed whitespace-pre-line">
                          {item.a}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default Faq;
